import React from 'react';
import { Alert, Tag } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

/**
 * Prefix for the client-side-only id given to an unregistered supplier.
 *
 * An unregistered supplier has no `vendor.id` — it is submitted as a free-text `vendor_name` — so the
 * picker needs *some* key to track and de-duplicate it in local state. This prefix makes such ids
 * unmistakable, so they can never be mistaken for a real vendor id and sent as `vendor_id`
 * (see `buildUnregisteredSupplierId` / `isUnregisteredSupplierId`).
 */
const UNREGISTERED_ID_PREFIX = 'unregistered:';

/**
 * Which party type the copy refers to. The mechanics are identical for suppliers and resellers; only
 * the wording and the supplier-intelligence caveat differ.
 */
export type PartyLabel = 'supplier' | 'reseller';

/**
 * Builds the client-side-only tracking id for an unregistered supplier. Case-insensitive by
 * construction, so adding "Acme" then "acme" collides and is caught as a duplicate.
 * @param {string} name - The supplier name as typed by the user.
 * @returns {string} A prefixed, lower-cased local id — never sent to the API as `vendor_id`.
 */
export function buildUnregisteredSupplierId(name: string): string {
  return `${UNREGISTERED_ID_PREFIX}${name.trim().toLowerCase()}`;
}

/**
 * Whether an id is a local unregistered-supplier key rather than a real `vendor.id`.
 * @param {string | number} id - The item id to test.
 * @returns {boolean} True when the id is a client-side unregistered key.
 */
export function isUnregisteredSupplierId(id: string | number): boolean {
  return String(id).startsWith(UNREGISTERED_ID_PREFIX);
}

/** Label shown on a supplier that exists only as free text until NPI provisions it. */
export const PENDING_SUPPLIER_LABEL = 'Provisioning pending';

/**
 * The explicit, NON-BLOCKING notice telling the user the supplier isn't registered in Vantage and
 * what happens next. Deliberately an `info` Alert rather than a field error: submission is allowed,
 * and a red/error treatment would read as "fix this before continuing" (AC-1).
 * @param {object} [props] - Component props.
 * @param {PartyLabel} [props.partyLabel] - Which party type the copy refers to; defaults to supplier.
 * @returns {React.ReactElement} The notice.
 */
export function UnregisteredSupplierNotice({
  partyLabel = 'supplier'
}: {
  partyLabel?: PartyLabel;
} = {}): React.ReactElement {
  return (
    <Alert
      type="info"
      showIcon
      icon={<ExclamationCircleOutlined />}
      message={`This ${partyLabel} isn't registered in Vantage`}
      description={
        `You can still submit the deal. We'll notify our team to add the ${partyLabel}, and it will ` +
        `show as "${PENDING_SUPPLIER_LABEL}" until they do.` +
        (partyLabel === 'supplier' ? ' Supplier intelligence and analytics stay unavailable for it until then.' : '')
      }
    />
  );
}

/**
 * The "Provisioning pending" tag shown beside an unregistered supplier wherever it is listed.
 * @returns {React.ReactElement} The tag.
 */
export function PendingSupplierTag(): React.ReactElement {
  return (
    <Tag color="orange" className="shrink-0">
      {PENDING_SUPPLIER_LABEL}
    </Tag>
  );
}
